// src/schemas/team.ts
import { z } from "zod";
import { TIME_ENTRY_TYPES, type TimeEntryType } from "@/lib/enums";
import type { PtoStatus } from "@/lib/pto/approval-rules";
import type { MonthStatsRow, TeamMember } from "@/lib/team/metrics";
import { isMonthParam } from "@/lib/team/months";
import type { PtoRequestView } from "@/lib/team/pto-list";
import type { TeamSignOffRow } from "@/lib/team/signoff-grid";
import type { TimeOffRow } from "@/lib/team/time-off";
import type { IsoDate } from "@/lib/time/dates";
import { monthSearchSchema } from "@/schemas/month";

/**
 * My Team API inputs and responses. No input carries the manager: the server takes it from the
 * session (Foundation `withPerson`). A report id is only a filter inside the manager's team.
 */

const GUID = /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/;

export const teamIdSchema = z
  .string()
  .regex(GUID, "Unknown id")
  .transform((v) => v.toUpperCase());

export const monthQuerySchema = z.strictObject({
  month: z.string().refine(isMonthParam, "Pick a month"),
});

export const ptoDecisionInputSchema = z.strictObject({ requestId: teamIdSchema });
export type PtoDecisionInput = z.infer<typeof ptoDecisionInputSchema>;

export const ptoRejectInputSchema = z.strictObject({
  requestId: teamIdSchema,
  note: z.string().trim().min(1, "Add a reason").max(500, "Keep the reason under 500 characters"),
});
export type PtoRejectInput = z.infer<typeof ptoRejectInputSchema>;

export interface TeamMonthView {
  month: IsoDate;
  asOf: IsoDate;
  members: TeamMember[];
  stats: MonthStatsRow[];
}

export interface PersonMonthsView {
  personId: string;
  asOf: IsoDate;
  /** Three months before the page month through three after, oldest first. */
  months: MonthStatsRow[];
}

export interface GuidanceRow {
  demandPersonId: string;
  demandName: string;
  roleName: string | null;
  rate: number | null;
  isBillableDemand: boolean;
  assignmentActive: boolean;
  planned: number;
  logged: number;
  billable: number;
}

export interface PersonEntry {
  id: string;
  date: IsoDate;
  hours: number;
  comment: string | null;
  taskName: string;
  projectName: string | null;
  demandName: string | null;
  type: TimeEntryType;
  billableAmount: number | null;
}

export interface TimeOffView {
  month: IsoDate;
  today: IsoDate;
  members: TeamMember[];
  rows: TimeOffRow[];
}

export interface SignOffGridView {
  today: IsoDate;
  /** Server clock as ISO; the grid computes lateness against it. */
  now: string;
  members: TeamMember[];
  rows: TeamSignOffRow[];
}

export interface PtoRequestsView {
  today: IsoDate;
  rows: PtoRequestView[];
}

export interface PtoDecisionResult {
  id: string;
  status: Extract<PtoStatus, "approved" | "rejected">;
  /** Time entries booked; null when booking failed (Retry shows). */
  booked: number | null;
  bookFailed: boolean;
  /** Why booking needs attention, when the screen should say more than the default retry text. */
  message?: string;
}

export interface PtoBookResult {
  id: string;
  booked: number;
}

/**
 * PTO items the signed-in person can decide, pending requests plus cancel asks
 * (src/api/team-views.ts getPendingApprovals). `count` = teamCount + hrCount drives the "N PTO requests to approve"
 * banner on Home and My Team, linking to `target`; the My Team nav badge shows `teamCount`, the
 * PTO nav badge `hrCount` (HR approver only). Shown only when > 0, never a zero tile.
 */
export interface PendingApprovalsView {
  count: number;
  teamCount: number;
  hrCount: number;
  target: "team" | "pto";
}

/**
 * My Team URL (UI standard 12: deep-linkable, survives a reload): `?month=`, `?view=details`
 * (none = Summary), `?pto=open` (PTO requests dialog, Home's Review), `?signoffs=open` (Time
 * sign-offs dialog), and the person panel: `?person=<PersonID>&panel=summary|guidance|entries`
 * with `&type=<entry type>` filtering its Time entries. The person is only a filter inside the
 * manager's own team; the server still scopes every read to the session's reports.
 */
export const teamSearchSchema = monthSearchSchema.extend({
  view: z.literal("details").optional().catch(undefined),
  pto: z.literal("open").optional().catch(undefined),
  signoffs: z.literal("open").optional().catch(undefined),
  person: z.string().regex(GUID).optional().catch(undefined),
  panel: z.enum(["summary", "guidance", "entries"]).optional().catch(undefined),
  type: z.enum(TIME_ENTRY_TYPES).optional().catch(undefined),
});
export type TeamSearch = z.infer<typeof teamSearchSchema>;
